import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLock, FaCheckCircle } from 'react-icons/fa';
import api from '../services/api';

// Page de réinitialisation du mot de passe. Lit ?token= dans l'URL, valide
// côté client (min. 6, correspondance) puis POST /api/auth/reset-password.
const ResetPassword = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('emailAuth.resetPassword.tooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('emailAuth.resetPassword.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.message || t('emailAuth.resetPassword.errorTitle'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7ff] to-[#faf8ff] flex flex-col items-center justify-center px-4">
      <div className="bg-white shadow-lg rounded-2xl p-8 w-full max-w-md text-center">
        {success ? (
          <>
            <div className="text-4xl mx-auto mb-4 text-white bg-gradient-to-br from-green-500 to-emerald-500 rounded-full w-16 h-16 flex items-center justify-center">
              <FaCheckCircle />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t('emailAuth.resetPassword.successTitle')}
            </h1>
            <p className="text-gray-600 mb-6">{t('emailAuth.resetPassword.successBody')}</p>
            <Link
              to="/signin"
              className="inline-block w-full py-2 text-white font-semibold rounded-md bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 transition"
            >
              {t('emailAuth.resetPassword.goToSignin')}
            </Link>
          </>
        ) : !token ? (
          <>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t('emailAuth.resetPassword.errorTitle')}
            </h1>
            <p className="text-gray-600 mb-6">{t('emailAuth.resetPassword.missingToken')}</p>
            <Link
              to="/forgot-password"
              className="inline-block w-full py-2 text-white font-semibold rounded-md bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 transition"
            >
              {t('emailAuth.resetPassword.requestNew')}
            </Link>
          </>
        ) : (
          <>
            <div className="text-4xl mx-auto mb-4 text-white bg-gradient-to-br from-purple-500 to-blue-500 rounded-full w-16 h-16 flex items-center justify-center">
              <FaLock />
            </div>
            <h1 className="text-3xl font-bold text-purple-600 mb-2">
              {t('emailAuth.resetPassword.title')}
            </h1>
            <p className="text-gray-600 mb-6">{t('emailAuth.resetPassword.subtitle')}</p>

            <form onSubmit={handleSubmit} className="space-y-4 text-start">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t('emailAuth.resetPassword.newPassword')}
                </label>
                <div className="flex items-center border rounded-md px-3 py-2 mt-1">
                  <FaLock className="text-gray-400 me-2" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="w-full outline-none bg-transparent"
                    placeholder={t('emailAuth.resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t('emailAuth.resetPassword.confirmPassword')}
                </label>
                <div className="flex items-center border rounded-md px-3 py-2 mt-1">
                  <FaLock className="text-gray-400 me-2" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="w-full outline-none bg-transparent"
                    placeholder={t('emailAuth.resetPassword.confirmPlaceholder')}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
              </div>

              {error && <div className="text-center text-sm text-red-500 mt-2">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-2 text-white font-semibold rounded-md bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 transition disabled:opacity-60"
              >
                {submitting
                  ? t('emailAuth.resetPassword.submitting')
                  : t('emailAuth.resetPassword.submit')}
              </button>
            </form>

            <p className="text-sm mt-6">
              <Link to="/signin" className="text-purple-600 hover:underline">
                {t('emailAuth.resetPassword.goToSignin')}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
